import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { getCheckoutStatus } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, CheckCircle2, XCircle, Shield, Zap, BarChart3 } from 'lucide-react';
import { BrandLogo } from '@/components/BrandLogo';

export default function StripeSuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('checking');
  const [attempts, setAttempts] = useState(0);
  const { refreshUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!sessionId) { setStatus('error'); return; }

    const poll = async () => {
      try {
        const { data } = await getCheckoutStatus(sessionId);
        if (data.payment_status === 'paid') {
          setStatus('success');
          await refreshUser();
          return;
        }
        if (data.status === 'expired') { setStatus('expired'); return; }
        if (attempts < 10) {
          setTimeout(() => setAttempts(a => a + 1), 2000);
        } else {
          setStatus('timeout');
        }
      } catch {
        setStatus('error');
      }
    };
    poll();
  }, [sessionId, attempts, refreshUser]);

  return (
    <div data-testid="stripe-success-page" className="min-h-screen bg-background flex">
      {/* Left Half - Dark */}
      <div className="hidden lg:flex lg:w-1/2 bg-[hsl(220,30%,12%)] p-12 flex-col justify-between relative overflow-hidden">
        {/* Background gradient accent */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-primary/10 rounded-full blur-[120px]" />
        
        <div className="relative z-10">
          <BrandLogo width={200} />
        </div>

        <div className="relative z-10 space-y-8">
          <h1 className="text-4xl font-bold text-white leading-tight" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Welcome to VendorSuite
          </h1>
          <p className="text-lg text-white/70">
            Your all-in-one platform for vendor compliance, document automation, and property management excellence.
          </p>

          <div className="space-y-4 pt-4">
            <div className="flex items-start gap-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/20 shrink-0">
                <Shield className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold text-white mb-1">Automated Compliance</h3>
                <p className="text-sm text-white/60">AI-powered document extraction and real-time compliance monitoring</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/20 shrink-0">
                <Zap className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold text-white mb-1">Lightning Fast</h3>
                <p className="text-sm text-white/60">Process hundreds of documents in minutes, not hours</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/20 shrink-0">
                <BarChart3 className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold text-white mb-1">Actionable Insights</h3>
                <p className="text-sm text-white/60">Track vendor status, renewals, and compliance metrics in real-time</p>
              </div>
            </div>
          </div>
        </div>

        <div className="relative z-10 text-white/40 text-xs">
          © 2026 VendorSuite. All rights reserved.
        </div>
      </div>

      {/* Right Half - Light with Status Card */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
        <Card className="max-w-md w-full rounded-xl shadow-[var(--shadow-soft)] border-2">
          <CardContent className="p-8 text-center">
            {/* Mobile Logo */}
            <div className="flex justify-center mb-6 lg:hidden">
              <BrandLogo width={180} />
            </div>

            {status === 'checking' && (
              <>
                <Loader2 className="mx-auto h-12 w-12 text-primary animate-spin mb-6" />
                <h2 className="text-2xl font-semibold mb-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Processing your payment...</h2>
                <p className="text-muted-foreground">Please wait while we confirm your subscription. This usually takes just a few seconds.</p>
              </>
            )}

            {status === 'success' && (
              <>
                <div className="flex h-16 w-16 mx-auto items-center justify-center rounded-full bg-[hsl(142,56%,36%)]/10 mb-6">
                  <CheckCircle2 className="h-10 w-10 text-[hsl(142,56%,36%)]" />
                </div>
                <h2 className="text-2xl font-semibold mb-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Payment Successful!</h2>
                <p className="text-muted-foreground mb-8">Your VendorSuite subscription is now active. You're all set to start managing your vendors with ease.</p>
                <Button data-testid="success-go-to-dashboard" onClick={() => navigate('/dashboard')} size="lg" className="w-full">
                  Go to Dashboard
                </Button>
              </>
            )}

            {(status === 'error' || status === 'expired' || status === 'timeout') && (
              <>
                <div className="flex h-16 w-16 mx-auto items-center justify-center rounded-full bg-destructive/10 mb-6">
                  <XCircle className="h-10 w-10 text-destructive" />
                </div>
                <h2 className="text-2xl font-semibold mb-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  {status === 'expired' ? 'Session Expired' : 'Payment Failed'}
                </h2>
                <p className="text-muted-foreground mb-8">
                  {status === 'expired' 
                    ? 'Your checkout session has expired. Please try again.' 
                    : 'We encountered an issue processing your payment. Please try again or contact support.'}
                </p>
                <Button variant="outline" onClick={() => navigate('/pricing')} size="lg" className="w-full">
                  Back to Pricing
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
